import React, { useState } from "react";

import {
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  MoreHoriz as MoreHorizIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Portal,
  Theme,
  Typography,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import Dialog from "@/components/Dialog";
import PlateCoverIcon from "@/components/Icons/PlateCoverIcon";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { Recipe } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import { useFragment } from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";

const useStyles = makeStyles()((theme: Theme) => ({
  fullWidth: {
    flexGrow: 1,
    marginInline: 1,
  },
  bold: {
    fontWeight: 700,
  },
  lightColor: {
    color: "rgba(125, 135, 151, 1)",
  },
  icon: {
    color: colors.primary,
    backgroundColor: "rgba(31,212,175,0.1)",
  },
  menuItem: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  menuItemTitle: {
    flex: 1,
  },
  menutItemIcon: {
    fontSize: 20,
    marginRight: theme.spacing(2),
  },
  deleteDialogIcon: {
    maxHeight: 60,
    fontSize: 60,
    color: colors.secondary,
  },
  deleteDialogTitle: {
    fontWeight: 700,
    fontSize: 28,
    lineHeight: 1,
    color: colors.dark,
    textAlign: "center",
  },

  deleteDialogButton: {
    minWidth: 125,
  },
}));

interface Props {
  id: Recipe["id"];
  onDeleteRecipe: (id: Recipe["id"]) => Promise<void>;
  onDuplicateRecipe: (id: Recipe["id"]) => Promise<void>;
}

const recipeListItemFragment = graphql(`
  fragment RecipeListItem on Recipe {
    id
    name
  }
`);

const RecipeListItem: React.FC<Props> = ({
  id,
  onDeleteRecipe,
  onDuplicateRecipe,
}) => {
  const { classes } = useStyles();
  const { pathname } = useLocation();
  const [recipe] = useFragment(recipeListItemFragment, `Recipe:${id}`);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuDialog = useDialogState();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [mode, setMode] = useState<string>("delete" || "duplicate");
  const modeMessage = mode == "delete" ? "Delete" : "Duplicate";
  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setIsSubmitting(false);
    setAnchorEl(null);
  };
  const handleDeleteRecipeConfirm = () => {
    setIsSubmitting(true);
    onDeleteRecipe(recipe?.id ?? "").then(menuDialog.hide);
    handleCloseMenu();
  };
  const handleDuplicateRecipeConfirm = () => {
    setIsSubmitting(true);
    onDuplicateRecipe(recipe?.id ?? "").then(menuDialog.hide);
    handleCloseMenu();
  };

  const handleMenuClick = (mode: string) => {
    menuDialog.show();
    setMode(mode);
  };
  return (
    <Box>
      <Box display="flex" alignItems={"center"} justifyContent="space-between">
        <Box display="flex" alignItems={"center"} mb={2}>
          <Box marginRight={4}>
            <Avatar className={classes.icon}>
              <PlateCoverIcon htmlColor={colors.gray} />
            </Avatar>
          </Box>
          <Typography variant="subtitle1" className={classes.bold}>
            {recipe?.name}
          </Typography>
        </Box>
        <Box alignSelf="flex-start">
          <IconButton onClick={handleOpenMenu} size="large">
            <MoreHorizIcon />
          </IconButton>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            PaperProps={{
              style: {
                width: "20ch",
              },
            }}
          >
            <MenuItem
              className={classes.menuItem}
              component={Link}
              to={`${pathname}/${recipe?.id}/edit`}
            >
              <EditIcon className={classes.menutItemIcon} color="inherit" />
              <Typography className={classes.menuItemTitle}>Edit</Typography>
            </MenuItem>
            <MenuItem
              className={classes.menuItem}
              onClick={() => handleMenuClick("duplicate")}
            >
              <FileCopyIcon className={classes.menutItemIcon} color="inherit" />
              <Typography className={classes.menuItemTitle}>
                Duplicate
              </Typography>
            </MenuItem>
            {Features.recipeDeleteEnabled && (
              <MenuItem
                className={classes.menuItem}
                onClick={() => handleMenuClick("delete")}
              >
                <DeleteForeverIcon
                  className={classes.menutItemIcon}
                  color="error"
                />
                <Typography className={classes.menuItemTitle}>
                  Delete
                </Typography>
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>
      <Divider light />
      <Portal>
        <Dialog
          open={menuDialog.shown}
          onClose={menuDialog.hide}
          content={
            <Box display="flex" flexDirection="column" alignItems="center">
              <div className={classes.deleteDialogIcon}>
                <WarningIcon fontSize="inherit" color="inherit" />
              </div>
              <Box marginBottom={5} />
              <Typography className={classes.deleteDialogTitle}>
                Are you sure?
              </Typography>
              <Box marginBottom={1} />
              <Typography fontWeight="bold" color={colors.gray}>
                {`Do you really want to ${modeMessage.toLowerCase()} this recipe?`}
              </Typography>
            </Box>
          }
          actions={
            <>
              <Button
                size="large"
                onClick={menuDialog.hide}
                sx={{ backgroundColor: "#E0E0E0", color: colors.black }}
              >
                Cancel
              </Button>
              <Button
                size="large"
                className={classes.deleteDialogButton}
                onClick={
                  mode == "delete"
                    ? handleDeleteRecipeConfirm
                    : handleDuplicateRecipeConfirm
                }
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress size="1.6rem" />
                ) : (
                  <>{modeMessage}</>
                )}
              </Button>
            </>
          }
        ></Dialog>
      </Portal>
    </Box>
  );
};

export default RecipeListItem;
